// ────────────────────────────────────────────────────────────────────
// RemainingLifeProjection — shared trend chart (extracted from
// AssetWorkspaceScreen, workbench roadmap C1): measured thickness history +
// forward projection to t-min, t-retirement/t-nominal reference lines, and
// the next-inspection marker. ONE implementation used by both the client
// asset workspace (real analysis) and the staff Desk (what-if calculator) —
// do not fork this again; extend it.
//
// TWO SOURCES, one set of maths:
//   • `c`     — one CML's history (the original source).
//   • `asset` — the asset's MINIMUM reading per inspection, which is what the
//               handoff's asset screen plots (design_handoff/README.md:181-187).
// The asset source brings the handoff's frame (viewBox 700×182, plot x 46→660,
// y 16→158) and its per-point label layer. It brings no second projection
// calculation: `retireT`, the due marker and the what-if line below are the
// same lines of code for both.
//
// Honest by construction: only historical points are drawn as measured data;
// the forward line is always a dashed PROJECTION at a stated rate, never
// implied as itself measured. `whatIf` layers a SECOND, distinctly-coloured
// dashed projection for a hypothetical rate (the Desk's live calculator) —
// it never replaces the real one, so "what's actually measured/derived" and
// "what I'm exploring" are always visually distinguishable (CLAUDE.md: mark
// assumed vs derived with different weight/colour, never blend them).
// ────────────────────────────────────────────────────────────────────
import { useMemo } from "react";
import type { CmlRow } from "@/lib/types";

export interface WhatIfProjection {
  rateMmYr: number;
  remainingLifeYears: number | null;
}

/** One inspection's governing (minimum) reading on the asset. */
export interface AssetReadingPoint {
  date: string;
  mm: number;
}

/** The asset-level source. Distinct from `CmlRow` on purpose: an asset has no
 *  single t-retirement or t-nominal to draw, and its series is one point per
 *  inspection rather than one per measured location. */
export interface AssetThicknessSeries {
  points: ReadonlyArray<AssetReadingPoint>;
  t_min_mm: number | null;
  governing_rate_mm_yr: number | null;
  remaining_life_years: number | null;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** UTC deliberately: an ISO date parses as UTC midnight, and reading it back in
 *  a timezone behind UTC would shift the label into the previous month — the
 *  same class of error as rounding a due date up a month. */
function monthYear(t: number): string {
  const d = new Date(t);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** Reduce raw per-location readings to one point per inspection date, keeping
 *  the MINIMUM. The asset is governed by its thinnest reading, never by a mean
 *  (design_handoff/README.md:342) — averaging here would quietly lengthen every
 *  interval the chart then draws. */
export function minReadingPerInspection(
  readings: ReadonlyArray<AssetReadingPoint>,
): AssetReadingPoint[] {
  const worst = new Map<string, number>();
  for (const r of readings) {
    if (!(r.mm > 0)) continue;
    const seen = worst.get(r.date);
    if (seen == null || r.mm < seen) worst.set(r.date, r.mm);
  }
  return [...worst.entries()]
    .map(([date, mm]) => ({ date, mm }))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
}

export function RemainingLifeProjection({ c, asset, nextDue, whatIf }: {
  c?: CmlRow; asset?: AssetThicknessSeries; nextDue?: string | null; whatIf?: WhatIfProjection | null;
}): JSX.Element {
  const assetMode = asset != null;
  const history: ReadonlyArray<AssetReadingPoint> = asset ? asset.points : (c?.history ?? []);
  const pts = useMemo(
    () => history.filter((h) => h.mm > 0).map((h) => ({ t: new Date(h.date).getTime(), mm: h.mm, date: h.date })).sort((a, b) => a.t - b.t),
    [history],
  );
  const rate = asset ? asset.governing_rate_mm_yr : (c?.governing_rate_mm_yr ?? null);
  const tmin = asset ? asset.t_min_mm : (c?.t_min_mm ?? null);
  const rl = asset ? asset.remaining_life_years : (c?.remaining_life_years ?? null);

  const hasTmin = tmin != null && tmin > 0;
  const rateUsable = rate != null && rate > 0 && rl != null && hasTmin;
  // MEASURED READINGS ARE DRAWN WHETHER OR NOT ANYTHING PROJECTS FORWARD, on
  // BOTH sources. The asset path already said so — "an ASSET with no rate still
  // has readings worth showing" — while the CML path bailed to a grey note the
  // moment `rate`, `rl` or `t_min` was missing, which is the normal shape of a
  // CML with one reading, a flat series (corrosion_rate_from_readings clamps
  // apparent negative loss to 0.0, and 0.0 fails `rate > 0`) or no minimum on
  // file. The chart it replaced, ThicknessTrend, plotted those readings and
  // annotated them; deleting it took the measured history off the screen for
  // every such CML — on the staff workspace and on the client-facing
  // GuestLandingScreen, which renders the same CmlDetail.
  //
  // So the fallback is now only for a chart with nothing measured to draw. What
  // a rate governs is the PROJECTION below — never the readings.
  if (pts.length < 1 || (assetMode && !hasTmin)) {
    return (
      <div className="px-4 py-6 font-mono text-center" style={{ border: "1px dashed var(--hairline)", background: "var(--paper-2)", fontSize: 11, color: "var(--ink-3)" }}>
        {assetMode
          ? "Thickness chart needs at least one measured reading and a t-min held on the asset — one of the two is missing here."
          : "No measured thickness readings on this CML yet — there is nothing to plot."}
      </div>
    );
  }
  // The projection and the due marker exist only where a MEASURED rate does.
  // A confident line drawn forward from a single reading is the visual form of
  // synthesising a rate, which design_handoff/README.md:319 forbids — so a
  // `nextDue` handed in for a rate-less asset is deliberately not drawn.
  const canProject = rateUsable && (!assetMode || (rl as number) > 0);

  const YR = 365.25 * 24 * 3600 * 1000;
  const last = pts[pts.length - 1];
  const retireT = canProject ? last.t + (rl as number) * YR : null;
  const dueT = nextDue ? new Date(nextDue).getTime() : null;
  // Past the guard `tmin` is known non-null in ASSET mode only; a CML may
  // legitimately hold none, and every line drawn against it is conditional.
  const tMin = hasTmin ? (tmin as number) : null;
  const tret = !assetMode && c?.t_retirement_mm && c.t_retirement_mm > 0 ? c.t_retirement_mm : null;
  const tnom = !assetMode && c?.t_nominal_mm && c.t_nominal_mm > 0 ? c.t_nominal_mm : null;

  // What-if: a hypothetical rate with a finite, computable remaining life —
  // draw its own retirement point out to whichever horizon is furthest so
  // both lines always fit the frame.
  const whatIfRetireT = (whatIf && whatIf.remainingLifeYears != null && Number.isFinite(whatIf.remainingLifeYears))
    ? last.t + whatIf.remainingLifeYears * YR : null;

  // Frame: the handoff's for the asset chart, the original for the CML one.
  // x 46→660 and y 16→158 fall out of the pads below.
  const { W, H, pad } = assetMode
    ? { W: 700, H: 182, pad: { l: 46, r: 40, t: 16, b: 24 } }
    : { W: 640, H: 220, pad: { l: 44, r: 64, t: 16, b: 30 } };
  const projEndT = retireT ?? (last.t + 2 * YR);
  const markerT = canProject ? dueT : null;
  const horizon = Math.max(projEndT, whatIfRetireT ?? 0, markerT ?? 0);
  const tLo = pts[0].t;
  const tHi = assetMode
    ? Math.max(projEndT + 0.6 * YR, last.t + 2 * YR, markerT ?? 0)
    : horizon + (projEndT - pts[0].t) * 0.04;
  const yHi = assetMode
    ? Math.max(...pts.map((p) => p.mm)) + 0.35
    : Math.max(...pts.map((p) => p.mm), tnom ?? 0) * 1.02;
  const yLo = assetMode
    ? Math.min(tmin as number, ...pts.map((p) => p.mm)) - 0.55
    : Math.min(...pts.map((p) => p.mm), ...(tMin != null ? [tMin] : [])) * 0.96;
  const x = (t: number) => pad.l + (t - tLo) / (tHi - tLo || 1) * (W - pad.l - pad.r);
  const y = (mm: number) => pad.t + (yHi - mm) / (yHi - yLo || 1) * (H - pad.t - pad.b);
  const yr = (t: number) => new Date(t).getFullYear();
  const histPath = pts.map((p, i) => `${i ? "L" : "M"}${x(p.t).toFixed(1)},${y(p.mm).toFixed(1)}`).join(" ");
  const showDue = markerT != null && markerT > tLo && markerT < tHi;

  // ── Asset-level chart: the handoff's, point-labelled ──
  // Colours are palette tokens, not the handoff's literals: #eeeae4→--line-2,
  // #dc2626→--err, #c25f05→--accent, #17150f→--ink-1 (portal/src/index.css).
  if (assetMode) {
    const rule = W - 32;   // the handoff runs its rules 8px past the plot box
    return (
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: "block", overflow: "visible" }}>
        <line x1={pad.l} x2={rule} y1={H - pad.b} y2={H - pad.b} stroke="var(--line-2)" strokeWidth={1} />

        {/* t-min, labelled at the left where the handoff puts it */}
        <line x1={pad.l} x2={rule} y1={y(tMin as number)} y2={y(tMin as number)} stroke="var(--err)" strokeWidth={1.2} strokeDasharray="6 4" />
        <text x={pad.l - 4} y={y(tMin as number) + 3} textAnchor="end" style={{ fontSize: 9, fontWeight: 600, fill: "var(--err)", fontFamily: "var(--mono)" }}>{(tMin as number).toFixed(2)}</text>
        <text x={pad.l - 4} y={20} textAnchor="end" style={{ fontSize: 9, fill: "var(--ink-4)", fontFamily: "var(--mono)" }}>{yHi.toFixed(1)}</text>

        {canProject && (
          <path data-projection="" d={`M${x(last.t).toFixed(1)},${y(last.mm).toFixed(1)} L${x(retireT as number).toFixed(1)},${y(tMin as number).toFixed(1)}`}
                fill="none" stroke="var(--accent)" strokeWidth={1.4} strokeDasharray="5 4" />
        )}
        {showDue && (<>
          <line data-due-marker="" x1={x(markerT as number)} y1={pad.t} x2={x(markerT as number)} y2={H - pad.b} stroke="var(--accent)" strokeWidth={1} strokeDasharray="3 3" />
          <text x={x(markerT as number) + 6} y={26} style={{ fontSize: 9, fontWeight: 600, fill: "var(--accent)", fontFamily: "var(--mono)" }}>
            NEXT DUE · {monthYear(markerT as number)}
          </text>
        </>)}

        {/* measured readings — one point per inspection, each labelled with its
            reading above and its date below */}
        <path d={histPath} fill="none" stroke="var(--ink-1)" strokeWidth={1.8} />
        {pts.map((p, i) => {
          const cur = i === pts.length - 1;
          return (
            <g key={p.date}>
              <circle data-point-marker="" data-date={p.date} cx={x(p.t)} cy={y(p.mm)} r={cur ? 5 : 3.6}
                      fill={cur ? "var(--accent)" : "var(--ink-1)"} stroke="var(--paper-2)" strokeWidth={1.5} />
              <text data-point-reading="" data-date={p.date} x={x(p.t)} y={y(p.mm) - 11} textAnchor="middle"
                    style={{ fontSize: 9.5, fontWeight: 600, fill: cur ? "var(--accent)" : "var(--ink-3)", fontFamily: "var(--mono)" }}>
                {p.mm.toFixed(2)}
              </text>
              <text data-point-date="" data-date={p.date} x={x(p.t)} y={H - 8} textAnchor="middle"
                    style={{ fontSize: 9, fill: "var(--ink-4)", fontFamily: "var(--mono)" }}>
                {monthYear(p.t)}
              </text>
            </g>
          );
        })}
      </svg>
    );
  }

  // ── CML-level chart (unchanged) ──
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: "block", border: "1px solid var(--hairline)", background: "var(--paper)" }}>
      {/* reference limit lines — each drawn only where the CML actually holds
          that limit. A t-min is nullable in practice (CmlRow.t_min_mm), and a
          line at y(null) is a limit invented for a point that has none. */}
      {tnom && <line x1={pad.l} x2={W - pad.r} y1={y(tnom)} y2={y(tnom)} stroke="var(--ink-4)" strokeWidth={1} strokeDasharray="2 3" opacity={0.5} />}
      {tret && <line x1={pad.l} x2={W - pad.r} y1={y(tret)} y2={y(tret)} stroke="#b06d12" strokeWidth={1} strokeDasharray="3 3" />}
      {tMin != null && (<>
        <line data-tmin-line="" x1={pad.l} x2={W - pad.r} y1={y(tMin)} y2={y(tMin)} stroke="var(--err)" strokeWidth={1.4} />
        <text x={W - pad.r + 4} y={y(tMin) + 3} style={{ fontSize: 8.5, fill: "var(--err)", fontFamily: "var(--mono)" }}>t-min {tMin.toFixed(2)}</text>
      </>)}
      {tret && <text x={W - pad.r + 4} y={y(tret) + 3} style={{ fontSize: 8, fill: "#b06d12", fontFamily: "var(--mono)" }}>t-ret</text>}
      {tnom && <text x={W - pad.r + 4} y={y(tnom) + 3} style={{ fontSize: 8, fill: "var(--ink-4)", fontFamily: "var(--mono)" }}>t-nom</text>}

      {/* projection: last measured point -> t-min at retirement (the real, derived rate) */}
      {canProject && (
        <line data-projection="" x1={x(last.t)} y1={y(last.mm)} x2={x(retireT as number)} y2={y(tMin as number)} stroke="var(--accent)" strokeWidth={1.6} strokeDasharray="4 3" />
      )}

      {/* what-if projection — a SECOND, visually distinct dashed line; never
          replaces the real one above (CLAUDE.md: assumed vs derived stay
          simultaneously legible, not swapped in place). */}
      {whatIfRetireT != null && tMin != null && (
        <>
          <line x1={x(last.t)} y1={y(last.mm)} x2={x(whatIfRetireT)} y2={y(tMin)}
                stroke="#7C3AED" strokeWidth={1.6} strokeDasharray="1.5 3.5" opacity={0.85} />
          <circle cx={x(whatIfRetireT)} cy={y(tMin)} r={3} fill="#7C3AED" />
          <text x={x(whatIfRetireT)} y={y(tMin) - 8} textAnchor="middle" style={{ fontSize: 8, fill: "#7C3AED", fontFamily: "var(--mono)", fontWeight: 700 }}>
            สมมติฐาน ~{yr(whatIfRetireT)}
          </text>
        </>
      )}

      {/* measured history */}
      <path d={histPath} fill="none" stroke="var(--ink-1)" strokeWidth={1.8} />
      {pts.map((p, i) => <circle key={i} cx={x(p.t)} cy={y(p.mm)} r={2.4} fill="var(--ink-1)" />)}

      {/* next inspection marker */}
      {showDue && (<>
        <line data-due-marker="" x1={x(markerT as number)} y1={pad.t} x2={x(markerT as number)} y2={H - pad.b} stroke="var(--accent)" strokeWidth={1} strokeDasharray="2 2" opacity={0.7} />
        <text x={x(markerT as number)} y={pad.t - 4} textAnchor="middle" style={{ fontSize: 8, fill: "var(--accent)", fontFamily: "var(--mono)" }}>next due {yr(markerT as number)}</text>
      </>)}

      {/* retirement point (derived rate) */}
      {canProject && (<>
        <circle cx={x(retireT as number)} cy={y(tMin as number)} r={3.2} fill="var(--err)" />
        <text x={x(retireT as number)} y={y(tMin as number) + 14} textAnchor="middle" style={{ fontSize: 8.5, fill: "var(--err)", fontFamily: "var(--mono)", fontWeight: 700 }}>retires ~{yr(retireT as number)}</text>
      </>)}

      {/* WHY NOTHING IS PROJECTED, said on the chart rather than left to be
          inferred from a missing dashed line. ThicknessTrend carried this note
          and it is the honest half of drawing the readings anyway. Pinned to
          the top margin band (y < pad.t) so it never collides with a reference
          line — every one of those sits at y(v) >= pad.t. */}
      {!canProject && (
        <text data-no-projection="" x={W / 2} y={10} textAnchor="middle" style={{ fontSize: 8, fill: "var(--ink-4)", fontFamily: "var(--mono)" }}>
          {pts.length === 1
            ? "single reading · trend needs a repeat survey"
            : tMin == null
              ? "measured history only · no t-min on file, so nothing projects forward"
              : "measured history only · no corrosion rate measured, so nothing projects forward"}
        </text>
      )}

      {/* x-axis year labels */}
      <text x={x(pts[0].t)} y={H - pad.b + 12} textAnchor="middle" style={{ fontSize: 8, fill: "var(--ink-4)", fontFamily: "var(--mono)" }}>{yr(pts[0].t)}</text>
      <text x={x(last.t)} y={H - pad.b + 12} textAnchor="middle" style={{ fontSize: 8, fill: "var(--ink-4)", fontFamily: "var(--mono)" }}>{yr(last.t)}</text>
    </svg>
  );
}
